/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle, CreditCard, Wallet, Loader2, ArrowRight } from 'lucide-react';
import { Product } from '../../../types/products';
import { Button } from '../../ui/Button';
import { ChainIcon } from '../../ui/ChainIcons';
import { createStoreOrder, payStoreOrder } from '../../../services/api';

interface CheckoutProps {
    items: { product: Product; quantity: number }[];
    onClose: () => void;
    onSuccess: () => void;
}

export const Checkout: React.FC<CheckoutProps> = ({ items, onClose, onSuccess }) => {
    const [method, setMethod] = useState<'crypto' | 'card'>('crypto');
    const [chain, setChain] = useState<'SOL' | 'SUI'>('SOL');
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState<'review' | 'processing' | 'success'>('review');

    const total = items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

    const handlePay = async () => {
        setIsLoading(true);
        try {
            // 1. Create Order
            const order = await createStoreOrder({
                items: items.map(i => ({ id: i.product.id, qty: i.quantity })),
                total,
                method
            });

            // 2. Simulate Payment Process
            setStep('processing');
            await new Promise(res => setTimeout(res, 2000));
            
            // 3. Confirm
            if (method === 'crypto') {
                await payStoreOrder(order.orderId, chain === 'SOL' ? 'SOL' : 'SUI', total);
            }
            
            setStep('success');
            setTimeout(() => {
                onSuccess();
            }, 2500);
        } catch (e) {
            console.error(e);
            setIsLoading(false);
        }
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
        >
            <motion.div 
                initial={{ scale: 0.9, y: 20 }} 
                animate={{ scale: 1, y: 0 }} 
                className="bg-white dark:bg-[#15171C] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-200 dark:border-[#2D3142] relative"
            >
                {step === 'review' && (
                    <>
                        <div className="p-6 border-b border-gray-100 dark:border-white/5 flex justify-between items-center">
                            <h2 className="font-serif text-2xl font-bold text-[#0A0B0F] dark:text-white">Secure Checkout</h2>
                            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors"><X size={20}/></button>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            <div className="flex justify-between items-center p-4 bg-gray-50 dark:bg-white/5 rounded-xl">
                                <span className="font-bold text-gray-500">Total to Pay</span>
                                <span className="font-mono text-2xl font-bold text-[#0A0B0F] dark:text-white">${total.toFixed(2)}</span>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-3">Payment Method</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button 
                                        onClick={() => setMethod('crypto')}
                                        className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${method === 'crypto' ? 'border-nobel-gold bg-nobel-gold/10 text-nobel-gold' : 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'}`}
                                    >
                                        <Wallet size={24} />
                                        <span className="font-bold text-sm">Crypto</span>
                                    </button>
                                    <button 
                                        onClick={() => setMethod('card')}
                                        className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-all ${method === 'card' ? 'border-nobel-gold bg-nobel-gold/10 text-nobel-gold' : 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'}`}
                                    >
                                        <CreditCard size={24} />
                                        <span className="font-bold text-sm">Card</span>
                                    </button>
                                </div>
                            </div>

                            {method === 'crypto' && (
                                <div className="animate-fade-in">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-3">Select Network</label>
                                    <div className="space-y-2">
                                        <button onClick={() => setChain('SOL')} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${chain === 'SOL' ? 'border-nobel-gold bg-nobel-gold/5' : 'border-gray-200 dark:border-white/10'}`}>
                                            <div className="flex items-center gap-3">
                                                <ChainIcon chain="SOL" />
                                                <span className="font-bold">Solana</span>
                                            </div>
                                            {chain === 'SOL' && <CheckCircle size={16} className="text-nobel-gold"/>}
                                        </button>
                                        <button onClick={() => setChain('SUI')} className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${chain === 'SUI' ? 'border-nobel-gold bg-nobel-gold/5' : 'border-gray-200 dark:border-white/10'}`}>
                                            <div className="flex items-center gap-3">
                                                <ChainIcon chain="SUI" />
                                                <span className="font-bold">Sui Network</span>
                                            </div>
                                            {chain === 'SUI' && <CheckCircle size={16} className="text-nobel-gold"/>}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-[#0A0B0F]">
                            <Button onClick={handlePay} size="lg" className="w-full" isLoading={isLoading}>
                                Pay ${total.toFixed(2)}
                            </Button>
                        </div>
                    </>
                )}

                {step === 'processing' && (
                    <div className="p-12 flex flex-col items-center justify-center text-center space-y-6">
                        <Loader2 size={64} className="text-nobel-gold animate-spin" />
                        <div>
                            <h3 className="text-xl font-bold text-[#0A0B0F] dark:text-white">Processing Payment</h3>
                            <p className="text-gray-500 mt-2">Confirming transaction on-chain...</p>
                        </div>
                    </div>
                )}

                {step === 'success' && (
                    <div className="p-12 flex flex-col items-center justify-center text-center space-y-6">
                        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                            <CheckCircle size={40} className="text-white" strokeWidth={3} />
                        </div>
                        <div>
                            <h3 className="text-2xl font-serif font-bold text-[#0A0B0F] dark:text-white">Order Confirmed!</h3>
                            <p className="text-gray-500 mt-2">Your receipt has been emailed.</p>
                        </div>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
};